// The command palette: the shortest path to any screen in the product.
//
// riksu had no search of any kind. On a product with eight destinations that is
// survivable; on one that grows a screen per module it is the difference between
// knowing the navigation and reading it every time.
//
// # It navigates and does NOT act
//
// Deliberately. A palette that can approve a leave request is a palette that can
// approve one by autocomplete, and every write in this product is behind a dialog with
// the facts on it for exactly that reason. So this goes places; the deciding still
// happens where the evidence is.
//
// A CREATE ENTRY IS STILL GOING SOMEWHERE. "Yeni izin talebi" takes somebody to `/leave`
// and opens the form; it fills nothing in and submits nothing, so the write stays behind
// the pane's own button with the facts on it. The line the rule above draws is between
// *arriving at a decision* and *making one by autocomplete*, and this is the first side
// of it.
//
// The entries come from `app/creations.ts` — the same declaration the topbar's `+`
// reads. Two lists would drift, and the palette is exactly where somebody looks for the
// thing that is missing from the menu.

import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { type MessageKey, useT } from "../shell/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

export interface Destination {
  readonly to: string;
  readonly label: MessageKey;
  readonly group: MessageKey;
}

/** One thing that can be created — from `app/creations.ts`, narrowed to this caller. */
export interface Creatable {
  readonly id: string;
  readonly label: MessageKey;
}

interface PaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly destinations: readonly Destination[];
  readonly creations: readonly Creatable[];
  readonly onCreate: (id: string) => void;
}

export function Palette({ open, onOpenChange, destinations, creations, onCreate }: PaletteProps) {
  const t = useT();
  const navigate = useNavigate();

  // ⌘K / Ctrl-K, from anywhere. `metaKey || ctrlKey` rather than sniffing the platform:
  // the wrong one does nothing on the platform it is not for, and a user agent string
  // is a worse guess than accepting both.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Grouped the way the rail is, so the palette and the navigation agree about what
  // belongs with what — two orderings of one product is two products to learn.
  const groups = [...new Set(destinations.map((destination) => destination.group))];

  return (
    // NOT `CommandDialog`. It is assembled here for two reasons a wrapper cannot
    // reach: it renders the generated `DialogContent`, so there is no way to ask for
    // the instant entrance this surface needs; and it does NOT wrap its children in
    // `Command`, which is what made the palette throw on open -- cmdk subscribed to a
    // store that was not there and React unmounted the entire product. Written out,
    // both are visible at the call site instead of inherited from a component whose
    // name suggests it handles them.
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader className="sr-only">
        <DialogTitle>{t("palette.title")}</DialogTitle>
        <DialogDescription>{t("palette.description")}</DialogDescription>
      </DialogHeader>
      {/* INSTANT, and that is the decision rather than an oversight. Opened by a
          keystroke, dozens to hundreds of times a day, and the deliberate shortest path
          to every destination -- an entrance here is latency paid on every invocation
          and skippable on none. It already read this way, by the dead-class bug; this
          is the same behaviour with a reason attached, so it survives somebody making
          the animations work. */}
      <DialogContent
        instant
        showCloseButton={false}
        className="top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0"
      >
        <Command>
          <CommandInput placeholder={t("palette.placeholder")} />
          <CommandList>
            <CommandEmpty>{t("palette.empty")}</CommandEmpty>
            {/* CREATING FIRST, mirroring the topbar where `+` sits before the search.
                Two orderings of one product is two products to learn, and this is the
                same argument the groups below are grouped by the rail's headings for.

                Absent entirely when the caller can create nothing — the same rule the
                `+` follows, because a heading over nothing is a promise the product
                does not keep. */}
            {creations.length > 0 ? (
              <CommandGroup heading={t("create.group")}>
                {creations.map((creation) => (
                  <CommandItem
                    key={creation.id}
                    // Matched on the TRANSLATED words, like every item here: matching on
                    // the identifier would mean typing "izin" finds nothing.
                    value={t(creation.label)}
                    onSelect={() => {
                      onOpenChange(false);
                      onCreate(creation.id);
                    }}
                  >
                    {t(creation.label)}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {groups.map((group) => (
              <CommandGroup key={group} heading={t(group)}>
                {destinations
                  .filter((destination) => destination.group === group)
                  .map((destination) => (
                    <CommandItem
                      key={destination.to}
                      // The VALUE is what the palette matches on, so it has to be the
                      // translated label. Matching on the path would mean typing "izin"
                      // finds nothing and typing "/leave" finds it.
                      value={t(destination.label)}
                      onSelect={() => {
                        onOpenChange(false);
                        navigate(destination.to);
                      }}
                    >
                      {t(destination.label)}
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
